// `useDashboardEvents` — Phase 3b. Single EventSource per dashboard,
// debounces incoming invalidations and fans out to every registered
// `refetch()` callback. Mounted once at DashboardPage.
//
// EventSource handles reconnect on transient drop. On prolonged
// disconnect (the readyState stays CLOSED for > 30s) the hook falls
// back to a 30s polling refetch — sandbox-cold-start / load-balancer
// idle cutoff scenarios.

import { useEffect, useRef } from "react";

import {
  DashboardEventSchema,
  type DashboardEvent,
} from "@loom/shared/dashboard";

export interface DashboardEventsHandlers {
  /**
   * Every callback registered here is invoked (debounced 250 ms) when
   * the server publishes a dashboard-invalidation event. Order is
   * insertion-order; one bad callback doesn't break the rest.
   */
  readonly refetch: ReadonlyArray<() => void>;
  /**
   * Optional predicate: when present, the refetchers only fire for events
   * it accepts. The per-issue page passes an iid filter so a write to
   * *another* issue doesn't trigger a refetch. Events without an iid (the
   * bridge stream-tap coarse fallback) should normally be accepted —
   * there's no way to know which issue they touched.
   */
  readonly filter?: (event: DashboardEvent) => boolean;
}

const DEBOUNCE_MS = 250;
const FALLBACK_POLL_MS = 30_000;

export const useDashboardEvents = (
  projectId: number,
  handlers: DashboardEventsHandlers,
): void => {
  // Capture the latest handler list in a ref so the EventSource closure
  // doesn't tear down + re-open every time the parent passes a new
  // array identity.
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    let cancelled = false;
    let debounceTimer: number | null = null;
    let pollTimer: number | null = null;
    let source: EventSource | null = null;

    const fireRefetchers = (): void => {
      if (cancelled) return;
      for (const fn of handlersRef.current.refetch) {
        try {
          fn();
        } catch {
          // One bad refetcher shouldn't sink the rest.
        }
      }
    };

    const schedule = (): void => {
      if (debounceTimer !== null) window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        debounceTimer = null;
        fireRefetchers();
      }, DEBOUNCE_MS);
    };

    const startFallbackPoll = (): void => {
      if (pollTimer !== null) return;
      pollTimer = window.setInterval(() => {
        fireRefetchers();
      }, FALLBACK_POLL_MS);
    };

    const stopFallbackPoll = (): void => {
      if (pollTimer !== null) {
        window.clearInterval(pollTimer);
        pollTimer = null;
      }
    };

    try {
      source = new EventSource(`/api/projects/${projectId}/events`, {
        withCredentials: true,
      });
      source.addEventListener("invalidate", (raw: MessageEvent<string>) => {
        try {
          const parsed = DashboardEventSchema.safeParse(JSON.parse(raw.data));
          if (!parsed.success) return;
          const accept = handlersRef.current.filter;
          if (accept && !accept(parsed.data)) return;
          schedule();
        } catch {
          // Malformed invalidation frame — skip rather than poisoning.
        }
      });
      let hadOpened = false;
      source.onopen = (): void => {
        stopFallbackPoll();
        // Resync after a reconnect gap: every invalidation emitted while the
        // stream was down (silent proxy kill, Cloud Run timeout, network
        // change) is gone for good — SSE has no replay — so the data may be
        // arbitrarily stale the moment the stream is healthy again. Skipped
        // on the FIRST open (consumers fetch on mount; refetching here would
        // just double the initial load).
        if (hadOpened) fireRefetchers();
        hadOpened = true;
      };
      source.onerror = (): void => {
        // EventSource auto-reconnects in `CONNECTING` state; only switch
        // on the fallback poll if we're fully closed.
        if (source && source.readyState === EventSource.CLOSED) {
          startFallbackPoll();
        }
      };
    } catch {
      // EventSource not available (SSR, ancient browser) — fall back
      // straight to polling.
      startFallbackPoll();
    }

    return () => {
      cancelled = true;
      if (debounceTimer !== null) window.clearTimeout(debounceTimer);
      stopFallbackPoll();
      source?.close();
    };
  }, [projectId]);
};
